import api from './api';
import type { Event } from './events';
import { horasDoPeriodo, janelaDeHistorico, type PeriodKey } from '../lib/period';

// ============================================================
// banQueue — a fila de decisão e o histórico da conta
// ============================================================
// ✅ CONTRATO REAL, NÃO ASSUMIDO. Este arquivo foi escrito contra um contrato
// inventado e depois RECONCILIADO com o backend que a Fase 3 produziu em
// paralelo. Os tipos abaixo são transcrição de:
//   backend/src/services/BanQueueService.cpp  (BanQueueService::ToJson)
//   backend/src/services/BanQueuePolicy.h     (BanCandidate, QueuedEvidence)
//   backend/src/controllers/admin/BanQueueController.h  (query params)
//   backend/src/services/EventService.h       (EventListFilter, FASE 3)
//
// DUAS SUPERFÍCIES, e a divisão importa:
//
//   GET /api/v1/admin/ban-queue   → QUEM banir. Agregado por dono da ÉPOCA,
//        admitido por tier (Strong+), com a prova anexada. Só leitura.
//   GET /api/v1/admin/events      → O QUE a conta fez. Ganhou os filtros
//        `user_id` / `account_id` (também por época). É daqui que sai o
//        histórico por dia/semana/mês — a bucketização é do cliente.
//
// ⚠️ O QUE O BACKEND **NÃO** OFERECE, e a tela não finge oferecer:
//   • Busca por PEDAÇO do nome. `target` casa o valor completo. A tela de
//     Contas filtra por substring sobre os candidatos da janela (client-side)
//     e diz que é isso que está fazendo.
//   • hwid como alvo. A fila é `account | user`. Máquina sem conta não entra —
//     o que é coerente com a fila ser sobre QUEM, mas significa que os PCs
//     cujas sessões nunca reportaram identidade continuam invisíveis aqui.
// ============================================================

/** Tiers do RiskEvidencePolicy, como o backend os serializa. */
export type EvidenceTierName = 'ignore' | 'contextual' | 'strong' | 'confirmed';

/** Piso de admissão na fila. O backend REJEITA qualquer coisa abaixo de strong. */
export type MinTier = 'strong' | 'confirmed';

/** Vocabulário de `sanctions.target_kind` — 'user' é aceito pelo CHECK. */
export type TargetKind = 'account' | 'user';

export interface QueuedEvidence {
  tier: EvidenceTierName;
  /** Família do RiskEvidencePolicy — duas famílias Strong sustentam kick. */
  family: string;
  event_type: string;
  attack_category: string;
  method: string;
  qualifier: string;
  /** SUM(occurrence_count) do sinal na janela. */
  hits: number;
  severity_max: number;
  sessions: number;
  first_seen: string;
  last_seen: string;
  sample: {
    event_id: string;
    session_uuid: string;
    identity_epoch: number;
  };
  /**
   * Mapa PLANO de campos de prova, whitelist fechada no backend
   * (BanQueuePolicy.cpp::ProofFields). Nunca é o payload inteiro.
   * Chaves observadas: asset_path, expected_sha256, actual_sha256, status,
   * integrity_status, match_via, process_name, process_hash, dll_name,
   * dll_hash, signer_subject, driver_name, baseline_hash, current_hash,
   * disk_hash, memory_hash, signature_id, region_type, method.
   */
  proof: Record<string, string>;
}

export interface ActiveSanctionRef {
  uuid: string;
  sanction_type: string;
  created_at: string;
  active_until: string | null;
}

/**
 * De onde veio a identidade do alvo.
 *
 * 🔴 `declared` = o CLIENTE disse. `ORACULO_SetUserId` é export C da nossa
 * própria DLL: qualquer processo faz GetProcAddress e carimba a conta que
 * quiser. Foi assim que o adversário pôs uma vítima inocente no topo desta
 * fila, com prova ao lado e botão de banir, usando 2 requests HTTP.
 * `authenticated` = o SERVIDOR DO JOGO afirmou, com credencial própria, num
 * login verificado.
 */
export type IdentityTrust = 'authenticated' | 'declared';

export interface BanCandidate {
  target_kind: TargetKind;
  target_value: string;
  /** Ver IdentityTrust. Vem nas DUAS listas — `data` e `investigate`. */
  identity_trust: IdentityTrust;
  /**
   * A ÚNICA autorização de ban. É `kick_eligible && identity_trust ===
   * 'authenticated'`: a evidência sustenta expulsão E sabemos de quem ela é.
   * As duas perguntas são independentes, e a segunda foi a que faltou por uma
   * rodada inteira.
   */
  ban_actionable: boolean;
  /** Score do MESMO motor que decide kick (risk::Evaluate). */
  evidence_score: number;
  /** Uma família Confirmed OU duas Strong — "isto sustentaria expulsão". */
  kick_eligible: boolean;
  top_tier: EvidenceTierName;
  families: { confirmed: number; strong: number; contextual: number };
  total_hits: number;
  first_seen: string;
  last_seen: string;
  session_uuids: string[];
  active_sanction: ActiveSanctionRef | null;
  evidence: QueuedEvidence[];
}

export interface BanQueueResponse {
  generated_at: string;
  window_seconds: number;
  min_tier: MinTier;
  signals: {
    considered: number;
    /**
     * Sinais cuja época não tem dono conhecido — o Oráculo VIU a evidência e
     * não sabe de quem é. A tela MOSTRA este número: uma fila que parece
     * completa quando não está é o mesmo silêncio que deixou `account_id` em
     * 0/45 por meses sem ninguém notar.
     */
    unattributed: number;
  };
  counts: {
    candidates: number;
    confirmed: number;
    strong: number;
    kick_eligible: number;
    /** Candidatos cuja identidade é AUTO-DECLARADA — investigação, nunca ban. */
    declared: number;
    /** Destes, quantos teriam evidência para expulsão se soubéssemos quem são. */
    declared_kick_eligible: number;
    /** Quantos botões de ban esta tela tem direito de desenhar. */
    ban_actionable: number;
  };
  /**
   * 🔴 SÓ ALVOS COM IDENTIDADE AUTENTICADA. É sobre estes, e só estes, que a
   * tela pode oferecer ban.
   *
   * ⚠️ Enquanto o servidor do jogo não integrar o bind autenticado, isto vem
   * VAZIO — a frota inteira é `declared`. Não é bug e não é fila limpa: é o
   * sistema admitindo que ainda não sabe quem é ninguém. O que aparecia aqui
   * antes eram alvos que o próprio cliente escolheu.
   */
  data: BanCandidate[];
  /**
   * Evidência forte cuja atribuição saiu de uma afirmação do CLIENTE. Aparece
   * na tela com a prova inteira e SEM ação de ban.
   *
   * Duas listas em vez de um flag numa lista só, por decisão: um flag depende
   * de quem desenha a tela lembrar de checá-lo — e do próximo script de
   * operação, e do próximo `curl | jq '.data'`. A separação estrutural não
   * depende de ninguém lembrar.
   */
  investigate: BanCandidate[];
}

export interface BanQueueParams {
  /** 1..720. Derivado do período pela UI. */
  window_hours?: number;
  min_tier?: MinTier;
  limit?: number;
  /** Sinais de prova por candidato (1..50; 0 = todos). */
  evidence_limit?: number;
  target_kind?: TargetKind;
  /** Valor COMPLETO do account_id/user_id — não casa pedaço. */
  target?: string;
}

/**
 * GET /api/v1/admin/ban-queue
 *
 * Erros PROPAGAM. Numa tela de decisão de ban, engolir a falha e mostrar fila
 * vazia é pior que mostrar erro: "ninguém para banir" e "a consulta falhou"
 * levam a ações opostas, e a primeira é indistinguível de um dia limpo.
 */
export async function fetchBanQueue(params: BanQueueParams = {}): Promise<BanQueueResponse> {
  const resp = await api.get<BanQueueResponse>('/admin/ban-queue', {
    params: {
      window_hours: params.window_hours ?? 168,
      min_tier: params.min_tier ?? 'strong',
      limit: params.limit ?? 50,
      evidence_limit: params.evidence_limit ?? 10,
      target_kind: params.target_kind,
      target: params.target || undefined,
    },
  });
  return resp.data;
}

/** A fila da janela corrente do período escolhido. */
export function listBanQueue(
  period: PeriodKey,
  minTier: MinTier = 'strong',
): Promise<BanQueueResponse> {
  return fetchBanQueue({
    window_hours: horasDoPeriodo(period),
    min_tier: minTier,
    limit: 100,
    evidence_limit: 5,
  });
}

// ------------------------------------------------------------
// Histórico da conta — "registros feitos nelas"
// ------------------------------------------------------------
// SEM endpoint de agregação: é `/admin/events` filtrado por identidade, e a
// organização por dia/semana/mês acontece no cliente (lib/period.ts). Um
// endpoint que agregasse por balde teria que fixar no SQL a definição de
// "semana" e o fuso — e o operador está no Chile, não em UTC. Bucketizar no
// cliente mantém a granularidade trocável sem round-trip e sem uma segunda
// definição de semana para divergir da primeira.

export type IdentityKind = 'account_id' | 'user_id';

/** `account | user` (fila) ↔ `account_id | user_id` (busca de eventos). */
export function identityKindDoAlvo(kind: TargetKind): IdentityKind {
  return kind === 'account' ? 'account_id' : 'user_id';
}

export function rotuloDaIdentidade(kind: IdentityKind | TargetKind): string {
  return (kind === 'account' || kind === 'account_id') ? 'conta' : 'jogador';
}

export interface AccountHistoryParams {
  kind: IdentityKind;
  value: string;
  period: PeriodKey;
  /** Quantos baldes para trás. Default por granularidade (BALDES_PADRAO). */
  buckets?: number;
  limit?: number;
}

export interface AccountHistoryResponse {
  data: Event[];
  next_cursor?: number;
}

export async function listAccountEvents(
  params: AccountHistoryParams,
  agora: Date = new Date(),
): Promise<AccountHistoryResponse> {
  const janela = janelaDeHistorico(params.period, params.buckets, agora);
  const resp = await api.get<AccountHistoryResponse>('/admin/events', {
    params: {
      cursor: 0,
      limit: params.limit || 200,
      from: janela.from,
      to: janela.to,
      account_id: params.kind === 'account_id' ? params.value : undefined,
      user_id:    params.kind === 'user_id'    ? params.value : undefined,
    },
  });
  return resp.data;
}
